// MFA enforcement gate for the license-server console. Wraps the authenticated
// area: if MFA is enforced and the session isn't aal2, the user must either
// enrol an authenticator (no factor yet) or complete a step-up TOTP challenge
// (has a factor) before the app renders. Enrol/verify go client→GoTrue directly.
// The Express backend independently rejects writes below aal2, so this gate is
// UX, not the sole control.
import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getAalStatus } from '@/lib/mfa';
import { ReportButton } from '@/components/reporter';
import { markIntentionalSignOut } from '@/lib/session-signout';

const MFA_ENFORCED = import.meta.env.VITE_MFA_ENFORCED !== 'false';

type Mode = 'checking' | 'ok' | 'enroll' | 'challenge';

/**
 * A20 — the report affordance on the one authenticated screen with no chrome.
 *
 * Every blocking state of this gate renders INSTEAD of the whole console, so
 * AdminHeader's button is not on screen. An operator whose authenticator is on a
 * dead phone, whose clock has drifted, or whose enrolment will not complete is
 * locked out with a verified account — and until this existed, the only way to
 * say so was out-of-band.
 *
 * <ReportButton> renders NOTHING when no <ReportProvider> is above it, so this
 * is never a dead control: App.tsx's <ProtectedArea> mounts <ReporterHost> above
 * this gate precisely so it is not. If that ordering is ever undone, this
 * disappears rather than drawing a button that opens nothing.
 *
 * `ps-no-capture` is rrweb's blockClass — the reporter's own UI is never part of
 * what the reporter records. It joins the two fences already on this screen (the
 * enrolment QR and the manual TOTP key, below), which are now genuinely
 * load-bearing: with the recorder mounted above this gate, this screen is
 * recorded for the first time.
 */
function Card({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-secondary)' }}>
      <div style={{ width: 440, background: 'var(--surface-raised)', borderRadius: 16, padding: 40, boxShadow: 'var(--shadow-modal)', border: '1px solid var(--border)' }}>
        {children}
        <div className="ps-no-capture" style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <ReportButton />
        </div>
      </div>
    </div>
  );
}

export function MfaGate({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<Mode>(MFA_ENFORCED ? 'checking' : 'ok');

  // Enroll state
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const evaluate = useCallback(async () => {
    if (!MFA_ENFORCED) { setMode('ok'); return; }
    try {
      const aal = await getAalStatus();
      if (aal.isAal2 || aal.currentLevel === null) {
        // aal2 → done. currentLevel null (unknown/no MFA support) → fail open.
        setMode('ok');
      } else if (aal.hasFactor) {
        setMode('challenge');
      } else {
        setMode('enroll');
      }
    } catch {
      setMode('ok'); // fail open — never hard-block on a transient check error
    }
  }, []);

  useEffect(() => { evaluate(); }, [evaluate]);

  async function beginEnroll() {
    setError(''); setBusy(true);
    try {
      const { data: fl } = await supabase.auth.mfa.listFactors();
      const stale = (fl?.all ?? []).find((f) => f.factor_type === 'totp' && f.status === 'unverified');
      if (stale) await supabase.auth.mfa.unenroll({ factorId: stale.id }).catch(() => {});
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
      if (error) throw error;
      setQr(data.totp.qr_code); setSecret(data.totp.secret); setFactorId(data.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start enrollment');
    } finally { setBusy(false); }
  }

  useEffect(() => { if (mode === 'enroll' && !factorId) beginEnroll(); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  async function verify(existingFactor: boolean) {
    setError(''); setBusy(true);
    try {
      let fid = factorId;
      if (existingFactor) {
        const { data: fl, error: fErr } = await supabase.auth.mfa.listFactors();
        if (fErr) throw fErr;
        fid = fl?.totp?.[0]?.id ?? null;
        if (!fid) throw new Error('No authenticator found');
      }
      if (!fid) throw new Error('Enrollment not ready');
      const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId: fid });
      if (chErr) throw chErr;
      const { error: vErr } = await supabase.auth.mfa.verify({ factorId: fid, challengeId: ch.id, code: code.trim() });
      if (vErr) throw vErr;
      setCode('');
      await evaluate(); // now aal2 → 'ok'
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verification failed');
    } finally { setBusy(false); }
  }

  if (mode === 'ok') return <>{children}</>;

  if (mode === 'checking') {
    return <Card><div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Checking security…</div></Card>;
  }

  const codeInput = (
    <div>
      <input
        type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
        value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        placeholder="123456" autoFocus
        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 18, letterSpacing: '0.3em', textAlign: 'center', outline: 'none' }}
      />
    </div>
  );

  const errBox = error ? (
    <div style={{ padding: '10px 12px', borderRadius: 6, background: '#FEE2E2', color: '#DC2626', fontSize: 13, margin: '12px 0' }}>{error}</div>
  ) : null;

  if (mode === 'challenge') {
    return (
      <Card>
        <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Two-factor authentication</h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>Enter the 6-digit code from your authenticator app.</p>
        {errBox}
        {codeInput}
        <button onClick={() => verify(true)} disabled={busy || code.trim().length < 6}
          style={{ width: '100%', marginTop: 16, padding: 11, borderRadius: 8, background: busy ? 'var(--accent-muted)' : 'var(--accent)', color: 'white', fontSize: 14, fontWeight: 600, border: 'none', cursor: busy ? 'not-allowed' : 'pointer' }}>
          {busy ? 'Verifying…' : 'Verify'}
        </button>
        <button onClick={() => { markIntentionalSignOut(); supabase.auth.signOut(); }} style={{ width: '100%', marginTop: 10, padding: 8, background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>Sign out</button>
      </Card>
    );
  }

  // mode === 'enroll'
  return (
    <Card>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Set up two-factor authentication</h1>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>Two-factor authentication is required. Scan the QR code with an authenticator app, then enter the code.</p>
      {errBox}
      {/* ps-no-capture: rrweb blockClass. Forced-enrolment path — the QR data-URI
          and the manual key are the same TOTP shared secret. Fenced on the image
          and the <code>, not the gate, so the rest of the flow still replays. */}
      {qr ? (
        <img className="ps-no-capture" src={qr} alt="TOTP QR" width={180} height={180} style={{ display: 'block', margin: '0 auto 12px', background: 'white', padding: 8, border: '1px solid var(--border)', borderRadius: 8 }} />
      ) : <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Generating…</p>}
      {secret && <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, textAlign: 'center' }}>Manual key: <code className="ps-no-capture" style={{ fontFamily: 'monospace' }}>{secret}</code></p>}
      {codeInput}
      <button onClick={() => verify(false)} disabled={busy || !factorId || code.trim().length < 6}
        style={{ width: '100%', marginTop: 16, padding: 11, borderRadius: 8, background: busy ? 'var(--accent-muted)' : 'var(--accent)', color: 'white', fontSize: 14, fontWeight: 600, border: 'none', cursor: busy ? 'not-allowed' : 'pointer' }}>
        {busy ? 'Verifying…' : 'Verify & enable'}
      </button>
      <button onClick={() => { markIntentionalSignOut(); supabase.auth.signOut(); }} style={{ width: '100%', marginTop: 10, padding: 8, background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>Sign out</button>
    </Card>
  );
}
